import React from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import RichTextEditor from "@/components/ui/RichTextEditor";
import { axiosPrivate } from "@/api/axios";
import { getStoredScheduledFact, saveScheduledFact } from "../utils/scheduledFactStorage";

const editFactSchema = z.object({
  date: z
    .string()
    .min(1, "Date is required")
    .regex(/^(\d{4}-\d{2}-\d{2})$/, "Invalid date format"),
  title: z.string().min(1, "Title is required"),
  description: z.string().min(1, "Description is required"),
});

export interface FactEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fact: any | null;
  onSaved: () => void;
}

const extractYyyyMmDd = (input?: unknown): string | undefined => {
  if (!input) return undefined;
  const raw = String(input);
  const match = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  if (match) return match[1];
  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    const yyyy = parsed.getFullYear();
    const mm = String(parsed.getMonth() + 1).padStart(2, "0");
    const dd = String(parsed.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }
  return undefined;
};

export default function FactEditDialog({ open, onOpenChange, fact, onSaved }: FactEditDialogProps) {
  const form = useForm<{ date: string; title: string; description: string }>({
    resolver: zodResolver(editFactSchema),
    values: {
      date: (() => {
        const today = new Date();
        const todayString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(
          today.getDate()
        ).padStart(2, "0")}`;

        if (fact?.id) {
          const stored = getStoredScheduledFact(fact.id);
          if (stored?.scheduled_date) {
            return stored.scheduled_date;
          }
        }

        const apiDate = extractYyyyMmDd((fact as any)?.scheduled_date) ?? extractYyyyMmDd((fact as any)?.created_at);
        return apiDate ?? todayString;
      })(),
      title: fact?.title ?? "",
      description: fact?.description ?? "",
    },
  });

  React.useEffect(() => {
    const today = new Date();
    const todayString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(
      today.getDate()
    ).padStart(2, "0")}`;
    let initialDate = todayString;

    if (fact?.id) {
      const stored = getStoredScheduledFact(fact.id);
      if (stored?.scheduled_date) {
        initialDate = stored.scheduled_date;
      }
    }

    if (!initialDate || initialDate === todayString) {
      const apiDate = extractYyyyMmDd((fact as any)?.scheduled_date) ?? extractYyyyMmDd((fact as any)?.created_at);
      if (apiDate) {
        initialDate = apiDate;
      }
    }

    form.reset({
      date: initialDate,
      title: fact?.title ?? "",
      description: fact?.description ?? "",
    });
  }, [fact, form]);

  const [submitting, setSubmitting] = React.useState(false);

  async function onSubmit(values: { date: string; title: string; description: string }) {
    if (!fact?.id) return;

    try {
      setSubmitting(true);

      const today = new Date();
      const todayString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(
        today.getDate()
      ).padStart(2, "0")}`;
      const isTodayDate = values.date === todayString;

      const payload = {
        title: values.title,
        description: values.description,
        type: fact.type,
        is_tod: isTodayDate,
      };

      await axiosPrivate.put(`/facts/${fact.id}/`, payload);

      saveScheduledFact(fact.id, {
        id: fact.id,
        scheduled_date: values.date,
        type: fact.type,
        title: values.title,
      });

      toast.success("Fact updated");
      onSaved();
      onOpenChange(false);
    } catch (error) {
      toast.error("Failed to update fact");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="!max-w-[600px] !w-[95vw]">
        <div className="space-y-4">
          <h2 className="text-lg font-semibold">Edit Fact</h2>
          <Form {...form}>
            <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
              <FormField
                name="date"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Scheduled Date</FormLabel>
                    <FormControl>
                      <Input {...field} type="date" min={new Date().toISOString().split("T")[0]} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                name="title"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title</FormLabel>
                    <FormControl>
                      <Input placeholder="Update title" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                name="description"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <RichTextEditor
                        value={field.value || ""}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        placeholder="Update description"
                        minHeight="320px"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={!form.formState.isValid || submitting}>
                  {submitting ? "Saving…" : "Save Changes"}
                </Button>
              </div>
            </form>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
